import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

// Keyframes extracted from Figma frames Home / Home 2 / Home 5 (1920×1080 reference).
// Each blob carries its 3 design states; we animate Home→Home2→Home5→Home in a 12s loop.
type BlobFrame = { left: number; top: number; w: number; h: number; bg: string };
type BlobSpec = { home: BlobFrame; home2: BlobFrame; home5: BlobFrame };

const group2: BlobSpec[] = [
    // V596 — top-left big blob
    {
        home:  { left: -295,    top: -224.3,  w: 1180.72, h: 704.48, bg: '#BAE1FF' },
        home2: { left: -228,    top: -177,    w: 1240.93, h: 709.12, bg: '#101C41' },
        home5: { left: 1122.67, top: -238.28, w: 1187.97, h: 670.83, bg: '#015FA6' },
    },
    // V597
    {
        home:  { left: 1058.48, top: -169.37, w: 899.25,  h: 543.19, bg: '#6CD8FF' },
        home2: { left: -89.28,  top: 592.38,  w: 950.71,  h: 538.56, bg: '#015FA6' },
        home5: { left: 577.7,   top: 243.22,  w: 911.52,  h: 507.21, bg: '#015FA6' },
    },
    // V599
    {
        home:  { left: 114.42,  top: 373.82,  w: 1104.06, h: 665.85, bg: '#101C41' },
        home2: { left: 1236.97, top: 215.14,  w: 1167.24, h: 660.17, bg: '#101C41' },
        home5: { left: 250.07,  top: -23.24,  w: 1119.13, h: 621.74, bg: '#B1C8DA' },
    },
    // V598
    {
        home:  { left: 721.54,  top: 98.62,   w: 902.03,  h: 813.59, bg: '#000000' },
        home2: { left: 1138.86, top: 346.67,  w: 916.75,  h: 850.75, bg: '#000000' },
        home5: { left: 250.07,  top: -271,    w: 869.66,  h: 813.35, bg: '#000000' },
    },
    // V600
    {
        home:  { left: 114.42,  top: -272,    w: 902.03,  h: 813.59, bg: '#022334' },
        home2: { left: 218.6,   top: -107.51, w: 916.75,  h: 850.75, bg: '#777777' },
        home5: { left: 156,     top: -186.86, w: 869.66,  h: 813.35, bg: '#777777' },
    },
];

const group3: BlobSpec[] = [
    // V596
    {
        home:  { left: -295,    top: -224.3,  w: 1180.72, h: 704.48, bg: '#000000' },
        home2: { left: -214.26, top: -60.21,  w: 1240.93, h: 709.12, bg: '#101C41' },
        home5: { left: 237.55,  top: 288,     w: 999.33,  h: 643.08, bg: '#015FA6' },
    },
    // V597
    {
        home:  { left: 1058.48, top: -169.37, w: 899.25,  h: 543.19, bg: '#000000' },
        home2: { left: 360.7,   top: 649.46,  w: 950.71,  h: 538.56, bg: '#015FA6' },
        home5: { left: 679.18,  top: 376.82,  w: 752.11,  h: 507.21, bg: '#015FA6' },
    },
    // V599
    {
        home:  { left: 114.42,  top: 373.82,  w: 1104.06, h: 665.85, bg: '#101C41' },
        home2: { left: 556.93,  top: -67.8,   w: 1167.24, h: 660.17, bg: '#101C41' },
        home5: { left: -271,    top: 467.98,  w: 923.41,  h: 621.74, bg: '#B1C8DA' },
    },
    // V598
    {
        home:  { left: 721.54,  top: 98.62,   w: 902.03,  h: 813.59, bg: '#000000' },
        home2: { left: -228,    top: -60.35,  w: 916.75,  h: 850.75, bg: '#000000' },
        home5: { left: 379.4,   top: 435.25,  w: 810.85,  h: 695.43, bg: '#000000' },
    },
    // V600
    {
        home:  { left: 114.42,  top: -272,    w: 902.03,  h: 813.59, bg: '#022334' },
        home2: { left: 1439.97, top: -177,    w: 916.75,  h: 850.75, bg: '#777777' },
        home5: { left: 1115.46, top: 334.75,  w: 810.85,  h: 695.43, bg: '#777777' },
    },
];

// Convert design-space px (1920-wide reference) to vw so the layout scales with viewport.
const PX_TO_VW = 100 / 1920;
const toVw = (px: number) => `${(px * PX_TO_VW).toFixed(3)}vw`;

const Blob: React.FC<{ spec: BlobSpec; reduce: boolean }> = ({ spec, reduce }) => {
    const { home, home2, home5 } = spec;

    const dx = (f: BlobFrame) => toVw(f.left - home.left);
    const dy = (f: BlobFrame) => toVw(f.top - home.top);
    const sc = (f: BlobFrame) => f.w / home.w; // uniform scale approximation

    const animate = reduce
        ? {}
        : {
              x: [dx(home), dx(home2), dx(home5), dx(home)],
              y: [dy(home), dy(home2), dy(home5), dy(home)],
              scale: [1, sc(home2), sc(home5), 1],
              backgroundColor: [home.bg, home2.bg, home5.bg, home.bg],
          };

    return (
        <motion.div
            animate={animate}
            transition={{
                duration: 12,
                ease: 'easeInOut',
                repeat: Infinity,
                repeatType: 'loop',
                times: [0, 1 / 3, 2 / 3, 1],
            }}
            style={{
                position: 'absolute',
                left: toVw(home.left),
                top: toVw(home.top),
                width: toVw(home.w),
                height: toVw(home.h),
                background: home.bg,
                willChange: 'transform, background-color',
            }}
        />
    );
};

const SeamlessBackground: React.FC = () => {
    const [noiseDataUrl, setNoiseDataUrl] = useState<string>('');
    const prefersReducedMotion = useReducedMotion();
    const reduce = prefersReducedMotion ?? false;

    useEffect(() => {
        const canvas = document.createElement('canvas');
        canvas.width = 500;
        canvas.height = 500;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            const imageData = ctx.createImageData(canvas.width, canvas.height);
            const data = imageData.data;
            for (let i = 0; i < data.length; i += 4) {
                const noise = Math.random() * 255;
                data[i] = noise;
                data[i + 1] = noise;
                data[i + 2] = noise;
                data[i + 3] = 255;
            }
            ctx.putImageData(imageData, 0, 0);
            setNoiseDataUrl(canvas.toDataURL('image/png'));
        }
    }, []);

    return (
        <div
            className="absolute left-0 right-0 top-0 bottom-0 pointer-events-none overflow-hidden"
            style={{ zIndex: 0 }}
        >
            {/* Base color */}
            <div style={{ position: 'absolute', inset: 0, background: '#022334' }} />

            {/* Group 2 — single blur pass, normal blend */}
            <div
                className="hidden sm:block"
                style={{
                    position: 'absolute',
                    inset: 0,
                    filter: 'blur(120px)',
                }}
            >
                {group2.map((spec, i) => (
                    <Blob key={`g2-${i}`} spec={spec} reduce={reduce} />
                ))}
            </div>

            {/* Group 3 — single blur pass, multiply blend */}
            <div
                className="hidden sm:block"
                style={{
                    position: 'absolute',
                    inset: 0,
                    filter: 'blur(120px)',
                    mixBlendMode: 'multiply',
                }}
            >
                {group3.map((spec, i) => (
                    <Blob key={`g3-${i}`} spec={spec} reduce={reduce} />
                ))}
            </div>

            {/* Mobile simplified gradient (no animation) */}
            <div
                className="sm:hidden"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '100vh',
                    background: `
                        radial-gradient(ellipse 400px 350px at 50% 30%,
                            rgba(108, 216, 255, 0.45) 0%,
                            transparent 60%
                        ),
                        radial-gradient(ellipse 350px 300px at 20% 60%,
                            rgba(186, 225, 255, 0.35) 0%,
                            transparent 60%
                        )
                    `,
                    filter: 'blur(60px)',
                }}
            />

            {/* Lower-page static groups (preserved from prior implementation) */}
            <div
                className="hidden sm:block"
                style={{
                    position: 'absolute',
                    top: '2400px',
                    left: 0,
                    right: 0,
                    height: '2002px',
                    background: `
                        radial-gradient(ellipse 900px 750px at 76% 65%,
                            rgba(1, 95, 166, 0.5) 0%,
                            transparent 50%
                        ),
                        radial-gradient(ellipse 850px 700px at 61% 69%,
                            rgba(1, 95, 166, 0.5) 0%,
                            transparent 50%
                        ),
                        radial-gradient(ellipse 800px 750px at 53% 61%,
                            rgba(177, 200, 218, 0.5) 0%,
                            transparent 50%
                        ),
                        radial-gradient(ellipse 750px 650px at 16% 34%,
                            rgba(0, 0, 0, 0.5) 0%,
                            transparent 50%
                        ),
                        radial-gradient(ellipse 700px 600px at 11% 39%,
                            rgba(119, 119, 119, 0.5) 0%,
                            transparent 50%
                        )
                    `,
                    filter: 'blur(120px)',
                }}
            />

            <div
                className="hidden sm:block"
                style={{
                    position: 'absolute',
                    top: '3600px',
                    left: 0,
                    right: 0,
                    height: '2064px',
                    background: `
                        radial-gradient(ellipse 700px 650px at 25% 66%,
                            rgba(1, 95, 166, 0.5) 0%,
                            transparent 50%
                        ),
                        radial-gradient(ellipse 850px 700px at 59% 51%,
                            rgba(1, 95, 166, 0.5) 0%,
                            transparent 50%
                        ),
                        radial-gradient(ellipse 750px 700px at 17% 60%,
                            rgba(177, 200, 218, 0.5) 0%,
                            transparent 50%
                        ),
                        radial-gradient(ellipse 750px 650px at 20% 45%,
                            rgba(0, 0, 0, 0.5) 0%,
                            transparent 50%
                        ),
                        radial-gradient(ellipse 800px 700px at 58% 40%,
                            rgba(119, 119, 119, 0.5) 0%,
                            transparent 50%
                        )
                    `,
                    mixBlendMode: 'multiply',
                    filter: 'blur(120px)',
                }}
            />

            <div
                className="hidden sm:block"
                style={{
                    position: 'absolute',
                    top: '4800px',
                    left: 0,
                    right: 0,
                    height: '1725px',
                    background: `
                        radial-gradient(ellipse 700px 650px at 21% 40%,
                            rgba(1, 95, 166, 0.5) 0%,
                            transparent 50%
                        ),
                        radial-gradient(ellipse 800px 700px at 36% 36%,
                            rgba(1, 95, 166, 0.5) 0%,
                            transparent 50%
                        ),
                        radial-gradient(ellipse 850px 750px at 44% 45%,
                            rgba(177, 200, 218, 0.5) 0%,
                            transparent 50%
                        ),
                        radial-gradient(ellipse 800px 700px at 82% 76%,
                            rgba(0, 0, 0, 0.5) 0%,
                            transparent 50%
                        ),
                        radial-gradient(ellipse 750px 650px at 87% 71%,
                            rgba(119, 119, 119, 0.5) 0%,
                            transparent 50%
                        )
                    `,
                    filter: 'blur(120px)',
                }}
            />

            {/* Soft-light noise overlay (~30% per spec) */}
            {noiseDataUrl && (
                <div
                    className="opacity-20 sm:opacity-30"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `url(${noiseDataUrl})`,
                        backgroundRepeat: 'repeat',
                        backgroundSize: '500px 500px',
                        mixBlendMode: 'soft-light',
                        pointerEvents: 'none',
                    }}
                />
            )}
        </div>
    );
};

export default SeamlessBackground;
